import Vue from 'vue'
import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import i18n from '@vue-storefront/i18n'
import { currentStoreView, localizedRoute } from '@vue-storefront/core/lib/multistore'
import omit from 'lodash-es/omit'
import RootState from '@vue-storefront/core/types/RootState'
import CartState from '../types/CartState'
import { Logger } from '@vue-storefront/core/lib/logger'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import { router } from '@vue-storefront/core/app'
import { isServer } from '@vue-storefront/core/helpers'
import config from 'config'
import getApiEndpointUrl from '@vue-storefront/core/helpers/getApiEndpointUrl'
import rootStore from '@vue-storefront/core/store'
import { request as customerRequest } from '@vue-storefront/core/lib/customerRequest'
import { CART_TOKEN_COOKIE, cartTokenFromCookie, writeCartTokenCookie } from '../helpers/cartTokenCookie'
var fetch = require('isomorphic-fetch')
import { processURLAddress } from '@vue-storefront/core/helpers'
import { customerAuthHeaders } from '@vue-storefront/core/modules/user/helpers/customerSession'

function _getDifflogPrototype () {
  return { items: [], serverResponses: [], clientNotifications: [] }
}

/** @todo: move this metod to data resolver; shouldn't be a part of public API no more */
async function _connect () {
  let location = rootStore.getters['locations/getActiveLocation'] ? rootStore.getters['locations/getActiveLocation'].code : router.currentRoute.params.store
  return await customerRequest(getApiEndpointUrl(config.cart, 'create_endpoint'), 'POST', { location_code: location })
    .then(async res => {
      if (!res.result.data) {
        throw new Error(`Unable to load customer cart: ${res.result.message}`)
      }
      if (!isServer) {
        await Vue.prototype.$db.cartsCollection.setItem('current-cart-token', res.result.data.id)
        await Vue.$cookies.set('ct', res.result.data.id)
      }
      return res
    })
    .catch(err => console.error(err))
}

const actions: ActionTree<CartState, RootState> = {
  /** Disconnect the shipping cart from sync by clearing out the cart token */
  async disconnect ({ commit }) {
    commit(types.CART_LOAD_CART_SERVER_TOKEN, null)
    if (!isServer) {
      await Vue.prototype.$db.cartsCollection.removeItem('current-cart-token')
      writeCartTokenCookie(Vue.$cookies, null)
    }
  },
  /** Clear the cart content + re-connect to newly created guest cart */
  async clear ({ commit, dispatch, getters, rootGetters }, options = { recreateAndSyncCart: false, clearOnServer: true }) {
    await commit(types.CART_LOAD_CART, { grouped: [], count: 0 })
    if (options.recreateAndSyncCart) {
      await commit(types.CART_LOAD_CART_SERVER_TOKEN, null)
      await commit(types.CART_SET_ITEMS_HASH, null)
      await dispatch('connect')
      await dispatch('load')
    }
    if (!isServer) Vue.$cookies.set('cart', JSON.stringify([]))
    if (options.clearOnServer) {
      let location_code = rootStore.getters['locations/getActiveLocation'] ? rootStore.getters['locations/getActiveLocation'].code : router.currentRoute.params.store
      return await customerRequest(getApiEndpointUrl(config.cart, 'clearcart_endpoint'), 'POST', { location_code })
        .catch(err => console.error(err))
    }
  },
  /** @description this method is part of "public" cart API */
  async load ({ commit, getters, dispatch }, { forceClientState = false, refreshTotals = false, isUpdate = false }: {forceClientState?: boolean, refreshTotals?: boolean, isUpdate?: boolean} = {}) {
    if (isServer) return
    const storedItems = await Vue.prototype.$db.cartsCollection.getItem('current-cart')
    if (storedItems) {
      const count = storedItems.reduce((sum, gr) => sum + (gr.items || []).reduce((s, it) => s + (it.qty || 0), 0), 0)
      commit(isUpdate ? types.CART_LOAD_UPDATE : types.CART_LOAD_CART, { grouped: storedItems, count })
    }
    let cartToken = getters.getCartToken
    if (!cartToken) {
      cartToken = cartTokenFromCookie(Vue.$cookies.get(CART_TOKEN_COOKIE))
    }
    if (cartToken) { // previously set token
      commit(types.CART_LOAD_CART_SERVER_TOKEN, cartToken)
      Logger.info('Cart token received from cache.', 'cache', cartToken)()
      Logger.info('Syncing cart with the server.', 'cart')()
      rootStore.dispatch('ui/toggleCartSkeleton', !isUpdate)
      let location_code = rootStore.getters['locations/getActiveLocation'] ? rootStore.getters['locations/getActiveLocation'].code : router.currentRoute.params.store
      commit(types.CART_LOADING_STATE, true)
      return await customerRequest(getApiEndpointUrl(config.cart, 'pull_endpoint'), 'GET', { location_code, refresh_totals: refreshTotals })
        .then(res => {
          if (res.result.hasOwnProperty('message') && res.result.message.match(/Unauthorized/)) {
            dispatch('connect')
          }
          let quote = res.result.data
          if (quote && quote.hasOwnProperty('id')) {
            if (!isServer) {
              Vue.$cookies.set('ct', quote.id)
              Vue.prototype.$db.cartsCollection.setItem('current-cart-token', quote.id)
              let items = []
              quote.grouped.forEach(gr => gr.items.forEach(it => items.push({qty : it.qty, sku: it.sku, item_id: it.item_id, item_hash: it.item_hash, options: it.options ? it.options : []})))
              Vue.$cookies.set('cart', JSON.stringify(items))
            }
            commit(isUpdate ? types.CART_LOAD_UPDATE : types.CART_LOAD_CART, quote)
            commit(types.CART_LOAD_CART_SERVER_TOKEN, quote.id)
          }
        })
        .finally(() => {
          rootStore.dispatch('ui/toggleCartSkeleton', false)
          commit(types.CART_LOADING_STATE, false)
        })
        .catch(err => {
          console.error(err)
          commit(types.CART_DELIVERY_UNKNOWN)
        })
    } else {
      Logger.info('Creating server cart token', 'cart')()
      await dispatch('connect').catch(err => console.error(err))
    }
  },

  /** Get one single item from the client's cart */
  getItem ({ getters }, sku) {
    return getters.getCartItems.find(p => p.sku === sku)
  },
  goToCheckout ({commit}) {
    commit('ui/setSearchpanel', false, { root:true})
    router.push(localizedRoute('/checkout', currentStoreView().storeCode))
  },
  /** add item to the client's cart + sync with server if enabled @description this method is part of "public" cart API */
  async addItem ({ dispatch }, { productToAdd, forceServerSilence = false }) {
    let productsToAdd = []
    if (productToAdd.type_id === 'grouped') { // TODO: add bundle support
      productsToAdd = productToAdd.product_links.filter((pl) => { return pl.link_type === 'associated' }).map((pl) => { return pl.product })
    } else {
      productsToAdd.push(productToAdd)
    }
    return dispatch('addItems', { productsToAdd: productsToAdd, forceServerSilence })
  },
  /** add multiple items to the client's cart and execute single sync with the server when needed  @description this method is part of "public" cart API */
  async addItems ({ commit, dispatch, getters }, { productsToAdd, forceServerSilence = false }) {
    let productHasBeenAdded = false
    let productIndex = 0
    const diffLog = _getDifflogPrototype()
    for (let product of productsToAdd) {
      let stock = product.stock;
      if (typeof product === 'undefined' || product === null) continue
      product.qty && typeof product.qty !== 'number' ? product.qty = parseFloat(product.qty) : ''

      if (config.entities.optimize && config.entities.optimizeShoppingCart) {
        product = omit(product, ['configurable_children', 'configurable_options', 'media_gallery', 'description', 'category_ids', 'product_links', 'stock', 'description'])
      }
      if (product.errors !== null && typeof product.errors !== 'undefined') {
        let productCanBeAdded = true
        for (let errKey in product.errors) {
          if (product.errors[errKey]) {
            productCanBeAdded = false
            diffLog.clientNotifications.push({
              type: 'error',
              message: product.errors[errKey],
              action1: { label: i18n.t('OK') }
            })
          }
        }
        if (!productCanBeAdded) {
          continue
        }
      }
      product.stock = stock
      let itemsInStorage = []
      try {
        itemsInStorage = isServer ? [] : JSON.parse(Vue.$cookies.get('cart'))
      } catch (e) {
        console.error(e)
      }
      if (typeof itemsInStorage === 'string') itemsInStorage = []
      let record;
      getters.groupedCart.forEach(pr => {
         let currentItem = pr.items.find(p => {
          // compare products sku
          if (p.sku !== product.sku) {
            return false
          }
          // compare product options sku
          let isOptionsEqual = true
          let isOptionsInCartProduct = 'options' in p && p.options || 'selected_options' in p && p.selected_options
          let isOptionsInAddProduct = 'selected_options' in product && product.selected_options
          if (
            isOptionsInCartProduct
            && isOptionsInAddProduct
          ) {
            product.selected_options.forEach(option => {
              if (!option.hasOwnProperty('qty') || !option.qty) {
                option.qty = 1
              }
            })
            if (JSON.stringify(p.options,['sku']) !== JSON.stringify(product.selected_options,['sku']) || JSON.stringify(p.options, ['qty']) !== JSON.stringify(product.selected_options,['qty'])) {
              isOptionsEqual = false
            }
          } else if((isOptionsInCartProduct && !isOptionsInAddProduct) || (!isOptionsInCartProduct && isOptionsInAddProduct)) {
            isOptionsEqual = false
          }
          return isOptionsEqual
        })
        if (currentItem) record = currentItem
      })
      let itemHash:string = record ? record.item_hash : ''
      let itemId:number = record ? record.id : 0
      if (!itemId || !itemHash && itemsInStorage && itemsInStorage.length) {
        if (product.selected_options && product.selected_options.length) {
          product.selected_options.forEach(option => {
            if (!option.hasOwnProperty('qty') || !option.qty) {
              option.qty = 1
            }
          })
        }
        let itemStorage = itemsInStorage.find(el => {
          if (el.sku === product.sku) {
            if (el.options || product.selected_options) {
              return !(JSON.stringify(el.options, ['sku']) !== JSON.stringify(product.selected_options, ['sku']) || JSON.stringify(el.options, ['qty']) !== JSON.stringify(product.selected_options, ['qty']))
            }
            return el
          }
        })
        itemId = itemStorage ? itemStorage.item_id : 0
        itemHash = itemStorage ? itemStorage.item_hash : ''
        if (!record && itemStorage) record = itemStorage
      }
      const result = await dispatch('stock/queueCheck', { product: product, qty: record ? record.qty + 1 : (product.qty ? product.qty : 1) }, {root: true})// queueCheck returns control immediately and checks in the background; returning just the cached stock data; we're using it because cart/sync checks the stock anyway; but if cart.synchronize is disabeld or offline mode is enabled then this queued check could be usefull there is also `stock/check` actions that returns the exact values
      product.onlineStockCheckid = result.onlineCheckTaskId // used to get the online check result
      if (result.status === 'volatile') {
        diffLog.clientNotifications.push({
          type: 'warning',
          message: i18n.t('The system is not sure about the stock quantity (volatile). Product has been added to the cart for pre-reservation.'),
          action1: { label: i18n.t('OK') }
        })
      }
      if (result.status === 'out_of_stock') {
        diffLog.clientNotifications.push({
          type: 'error',
          message: i18n.t('The product is out of stock and cannot be added to the cart!'),
          action1: { label: i18n.t('OK') }
        })
      }

      if (result.status === 'ok' || result.status === 'volatile') {
        let productQty, newProductQty, canBeAdded = true
        let min_sale_qty_default = productsToAdd[productIndex].stock.is_qty_decimal ? 0.1 : 1
        let min_sale_qty = productsToAdd[productIndex].stock.is_qty_decimal ? productsToAdd[productIndex].stock.min_sale_qty : min_sale_qty_default
        productQty = productsToAdd[productIndex].qty === undefined ? min_sale_qty : productsToAdd[productIndex].qty
        newProductQty = productQty

        if (record) {
          canBeAdded = result.qty >= (record.qty + productQty)
          newProductQty = Math.abs(record.qty + productQty).toFixed(2)
        }

        if (result.qty > 0.1 && productQty <= result.qty && canBeAdded) {
          commit(types.CART_ADD_ITEM, { product })
          let cartToken = await Vue.prototype.$db.cartsCollection.getItem('current-cart-token')
          if (!cartToken) {
            dispatch('connect')
              .then(async res => {
                cartToken = await Vue.prototype.$db.cartsCollection.getItem('current-cart-token')
              })
          }
          let selected = product.selected_options,
            options = []
          if (selected) {
            selected.forEach(selectedOption => {
              let optionId = typeof selectedOption === 'number' ? selectedOption : selectedOption.option_type_id
              let value, option = product.custom_options.find(opt => {
                value = opt.values.find(ov => ov.option_type_id === optionId)
                return value
              })
              if (!product.qty) {
                product.qty = 1
              }
              let qtyOption = selectedOption && selectedOption.hasOwnProperty('qty') ? selectedOption.qty : 1
              options.push({
                option_id: option.option_id,
                name: option.title,
                price: value.price,
                sku: value.sku,
                title: value.title,
                qty: qtyOption,
              })
            })
          }
          let locationCode = product.location_code ? product.location_code : rootStore.getters['locations/getActiveLocation'].code,
            partnerCode = product.partner_code ? product.partner_code : rootStore.getters['partners/getCurrentPartnerCode']
          await customerRequest(getApiEndpointUrl(config.cart, 'updateitem_endpoint'), 'POST', {
            cartItem: {
              qty: newProductQty,
              sku: product.sku,
              options,
              item_hash: itemHash,
              item_id: itemId
            },
            location_code: locationCode,
            partner_code: partnerCode
          })
            .then(res => {
              if (res.code && res.code !== 200) {
                let notificationData = {
                  type: 'error',
                  message: i18n.t(res.result.message),
                  action1: { label: i18n.t('OK') },
                  action2: null
                }
                diffLog.clientNotifications.push(notificationData)
                throw new Error(res.result)
              }
              let notificationData = {
                type: 'success',
                message: i18n.t('Product has been added to the cart!'),
                action1: { label: i18n.t('OK') },
                action2: { label: i18n.t('Go to cart'),
                  action: () => {
                    dispatch('load')
                    dispatch('ui/toggleMicrocart', true, { root:true})
                  }}
              }
              let items = []
              try {
                items = JSON.parse(Vue.$cookies.get('cart'))
              } catch (e) {
                console.error(e)
              }
              items = items ? items : []
              let item = items.find(el => {
                if (el.sku === product.sku) {
                  if (el.options || product.selected_options) {
                    return !(JSON.stringify(el.options, ['sku']) !== JSON.stringify(product.selected_options, ['sku']) || JSON.stringify(el.options, ['qty']) !== JSON.stringify(product.selected_options, ['qty']))
                  }
                  return el
                }
              })
              if (!item) {
                item = {
                  qty: newProductQty,
                  sku: product.sku,
                  item_hash: res.result.data.item_hash,
                  item_id: res.result.data.item_id
                }
                if (product.selected_options && product.selected_options.length) {
                  item.options = product.selected_options
                }
                items.push(item)
              } else {
                item.qty = Number(newProductQty)
              }
              if (!isServer) Vue.$cookies.set('cart', JSON.stringify(items))

              diffLog.clientNotifications.push(notificationData)
              // res.result.data never carries a `grouped` array, so committing it via CART_LOAD_CART would wipe groupedCart to [].
              return dispatch('load', { isUpdate: true })
            })
            .catch(err => console.error(err))
        } else {
          let notificationData = {
            type: 'error',
            message: i18n.t('Requested qty is not available! There are only {qty} items left', {qty: result.qty}),
            action1: { label: i18n.t('OK') },
            action2: null
          }
          diffLog.clientNotifications.push(notificationData)
        }
      }
      productIndex++
    }
    return diffLog
  },
  /** remove single item from the server cart by payload.sku or by payload.product.sku @description this method is part of "public" cart API */
  async removeItem ({ commit, dispatch, getters }, payload) {
    let removeByParentSku = true // backward compatibility call format
    let product = payload
    if (payload.product) { // new call format since 1.4
      product = payload.product
      removeByParentSku = payload.removeByParentSku
    }
    commit(types.CART_DEL_ITEM, { product, removeByParentSku })
    let location_code = rootStore.getters['locations/getActiveLocation'] ? rootStore.getters['locations/getActiveLocation'].code : router.currentRoute.params.store
    return await customerRequest(getApiEndpointUrl(config.cart, 'deleteitem_endpoint'), 'POST', {
      cartItem: {
        item_id: product.item_id,
        location_code
      }
    }).then(res => {
      if (res) {
        let items = []
        try {
          items = isServer ? [] : JSON.parse(Vue.$cookies.get('cart'))
        } catch (e) {
          console.error(e)
        }
        if (items) {
          items.forEach((p, i) => {
            if (p.sku === product.sku) items.splice(i, 1)
          })
          if (!isServer) Vue.$cookies.set('cart', JSON.stringify(items))
        }
      }
      return res
    })
      .catch(err => console.error(err))
  },
  /** this action just updates the product quantity in the cart - by product.sku @description this method is part of "public" cart API */
  async updateQuantity ({ commit, dispatch, getters }, { product, qty, forceServerSilence = false }) {
    commit(types.CART_UPD_ITEM, { product, qty })
    let selected = product.selected_options && product.selected_options.length ? product.selected_options : product.options,
      options = []
    if (selected) {
      selected.forEach(selectedOption => {
        let value, option = product.custom_options.find(opt => {
          value = opt.values.find(ov => ov.option_type_id === selectedOption.option_type_id || ov.sku === selectedOption.sku)
          return value
        })
        options.push({
          option_id: option.option_id,
          name: option.title,
          price: value.price,
          sku: value.sku,
          title: selectedOption.title,
          qty: selectedOption && selectedOption.hasOwnProperty('qty') ? selectedOption.qty : 1
        })
      })
    }
    let locationCode = product.location_code ? product.location_code : rootStore.getters['locations/getActiveLocation'].code,
      partnerCode = product.partner_code ? product.partner_code : rootStore.getters['partners/getCurrentPartnerCode']
    return await customerRequest(getApiEndpointUrl(config.cart, 'updateitem_endpoint'), 'POST', {
      cartItem: {
        item_hash: product.item_hash,
        item_id: product.item_id,
        qty: qty,
        sku: product.sku,
        options
      },
      location_code: locationCode,
      partner_code: partnerCode
    })
      .then(res => {
        let items = []
        try {
          items = !isServer ? JSON.parse(Vue.$cookies.get('cart')): []
        } catch (e) {
          console.error(e)
        }

        items.find(it => product.sku === it.sku).qty = qty
        if (!isServer) Vue.$cookies.set('cart', JSON.stringify(items))
      })
      .catch(err => console.error(err))
  },
  /** this action merges in new product properties into existing cart item (by sku) @description this method is part of "public" cart API */
  updateItem ({ commit }, { product }) {
    commit(types.CART_UPD_ITEM_PROPS, { product })
  },
  /** remove discount code from the cart + sync totals @description this method is part of "public" cart API */
  async removeCoupon ({ getters, dispatch }) {
    if (getters.isTotalsSyncEnabled && getters.isCartConnected) {
      const task = await TaskQueue.execute({ url: getApiEndpointUrl(config.cart, 'deletecoupon_endpoint'),
        payload: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors'
        },
        silent: false
      })
      if (task.result) {
        dispatch('syncTotals', { forceServerSync: true })
        return task.result
      }
    }
    return null
  },
  /** add discount code to the cart + refresh totals @description this method is part of "public" cart API */
  async applyCoupon ({ getters, dispatch }, couponCode) {
    if (getters.isTotalsSyncEnabled && getters.isCartConnected) {
      const task = await TaskQueue.execute({ url: getApiEndpointUrl(config.cart, 'applycoupon_endpoint').replace('{{coupon}}', couponCode),
        payload: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors'
        },
        silent: true
      })
      if (task.result === true) {
        dispatch('syncTotals', { forceServerSync: true })
        return true
      }
    }
    return false
  },
  /** authorize the cart after user got logged in using the current cart token */
  authorize () {
    Vue.prototype.$db.usersCollection.getItem('last-cart-bypass-ts', (err, lastCartBypassTs) => {
      if (err) {
        Logger.error(err, 'cart')()
      }
    })
  },
  /** connect cart to the server and set the cart token */
  async connect ({ commit }) {
    return _connect()
      .then(async response => {
        if (response && response.code === 200) {
          const cartToken = response.result.data.id
          Logger.info('Server cart token created.', 'cart', cartToken)()
          commit(types.CART_LOAD_CART_SERVER_TOKEN, cartToken)
          if (rootStore.getters['address/getGuestAddresses'].length) {
            let guestAddresses = rootStore.getters['address/getGuestAddresses'][0]
            // Awaited so a fresh guest cart (e.g. right after checkout re-creates one) never
            // becomes usable before its address is re-attached server-side - otherwise delivery
            // pricing on the next partner's cart silently falls back to free (K2-2892).
            await rootStore.dispatch('address/updateGuestAddress', guestAddresses)
              .catch(err => Logger.error(err, 'cart')())
          }
          return response.result.data
        }
      })
  },
  toggleMicrocart ({ commit }) {
    commit(types.CART_TOGGLE_MICROCART)
  },
  async loadCount ({ commit, getters, dispatch }, { cartToken = '', token = '', location_code = '' }) {
    if (token === '') {
      commit(types.CART_USER_TOKEN_CHANGED)
      await dispatch('load')
      cartToken = getters.getCartToken
    }
    if (cartToken) {
      let url = getApiEndpointUrl(config.cart, 'count_load_endpoint')
      url = url.replace('{{token}}', '')
        .replace('{{cartId}}', cartToken)
        .replace('{{location_code}}', location_code)
      return await fetch(processURLAddress(url), { headers: customerAuthHeaders(token) }) // K2-3138
      .then(response => response.json())
      .then(res => {
        if (res && (res.result || res.result == 0) && typeof res.result === "number") {
          commit(types.CART_SET_COUNT, res.result)
          return res.result
        }
      })
      .catch(err => console.error(err))
    }
  },
  setCartToken ({ commit }, token) {
    commit(types.CART_LOAD_CART_SERVER_TOKEN, token)
  },
  async loadItemsCart ({ commit }, { cartToken = '', token = '', location_code = '' }) {
    let url = getApiEndpointUrl(config.cart, 'cart_get_item_endpoint')
    url = url.replace('{{token}}', '')
      .replace('{{cartId}}', cartToken)
      .replace('{{location_code}}', location_code)
    return await fetch(processURLAddress(url), { headers: customerAuthHeaders(token) }) // K2-3138
      .then(response => response.json())
      .then(res => {
        // only a 200 carries items; an error body ({code: 401, result: {message}}) must not reach the cart cookie (K2-3138)
        if (res && Number(res.code) === 200 && res.result && typeof res.result !== 'string') {
          if(!isServer) Vue.$cookies.set('cart', JSON.stringify(res.result))
          return res.result
        }
      })

  }
}

export default actions
